import { useEffect, useRef, useState } from "react";
import type { Language } from "../i18n";
import { t } from "../i18n";
import type { LocalScanHistoryItem } from "../state/storage";

export interface ScanHistoryPanelProps {
  items: LocalScanHistoryItem[];
  onSearch: (barcode: string) => void;
  language?: Language;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

/** Session scan list: a bottom chip that opens a sheet aligned with the result card. */
export function ScanHistoryPanel({
  items,
  onSearch,
  language = "ru",
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
}: ScanHistoryPanelProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : uncontrolled;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setExpanded = (next: boolean) => {
    if (!isControlled) setUncontrolled(next);
    onExpandedChange?.(next);
  };

  useEffect(() => {
    if (!expanded) return;

    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [expanded]);

  if (items.length === 0) return null;

  const openItem = (barcode: string) => {
    onSearch(barcode);
  };

  const count = items.length;
  const toggleLabel = t(language, "history_toggle_label", { count });

  if (!expanded) {
    return (
      <div className="scan-history scan-history--collapsed" data-testid="scan-history" ref={rootRef}>
        <button
          type="button"
          className="scan-history__toggle"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          aria-label={t(language, "history_toggle_expand_aria", { count })}
          data-testid="scan-history-toggle"
        >
          <span className="scan-history__toggle-text">{toggleLabel}</span>
          <span className="scan-history__count" data-testid="scan-history-count">
            {count}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="scan-history scan-history--expanded scan-sheet" data-testid="scan-history" ref={rootRef}>
      <div className="overlay__header">
        <div className="scan-history__title">{t(language, "history_title")}</div>
        <button
          type="button"
          className="overlay__close"
          onClick={() => setExpanded(false)}
          aria-label={t(language, "history_toggle_collapse_aria")}
          data-testid="scan-history-toggle"
        >
          ✕
        </button>
      </div>
      <ul className="scan-history__list">
        {items.map((item) => (
          <li key={item.id} className="scan-history__item" data-testid="scan-history-item">
            <button
              type="button"
              className="scan-history__open"
              onClick={() => openItem(item.barcode)}
              aria-label={t(language, "history_open_aria", { name: item.name || item.barcode })}
              data-testid="scan-history-open"
            >
              <span className="scan-history__copy">
                <span className="scan-history__name">{item.name || item.barcode}</span>
                {item.name ? <span className="scan-history__barcode">{item.barcode}</span> : null}
              </span>
              <span className="scan-history__arrow" aria-hidden="true">
                →
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
